import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Ban, ChevronDown, ChevronLeft, ChevronRight, Database, Search, SearchX, X } from "lucide-react";
import { toast } from "react-toastify";
import { getAdminReferralPartners, type AdminReferralPartner, type AdminUserSummary } from "../services/UserService";
import { USER_PLAN_FILTER_LABELS, userMatchesPlanFilter, type UserPlanFilter } from "./adminTabs/userPlanFilters";
import GodModeActivator from "./GodModeActivator";

/** Sentinel for users with no referralId — organic / DealHawk. */
export const DEALHAWK_REFERRAL_FILTER = "__dealhawk__";

/** Old attribution tokens were UUIDs; only show short Tolt ref codes in the filter. */
const isLegacyReferralId = (ref: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ref.trim());

const AVAILABLE_RETAILERS = [
  { value: "walmart", label: "Walmart" },
  { value: "home_depot", label: "Home Depot" },
  { value: "target", label: "Target" },
  { value: "dollar_general", label: "Dollar General" },
] as const;

interface UsersTableProps {
  users: AdminUserSummary[];
  loading?: boolean;
  onUserSelect: (userId: string) => void;
  onCreateUser?: () => void;
  planFilter?: UserPlanFilter | null;
  onPlanFilterChange?: (filter: UserPlanFilter | null) => void;
}

type TableUser = {
  userId: string;
  name: string;
  banned?: boolean;
  discordUserId?: string;
  created?: number;
  retailers: string[];
  referralId: string;
  searchText: string;
};

const mapToTableUser = (user: AdminUserSummary): TableUser => ({
  userId: user.userId,
  name: user.name || "",
  banned: user.banned,
  discordUserId: user.discordUserId,
  created: user.created,
  retailers: user.retailers || [],
  referralId: (user.referralId || "").trim(),
  searchText: [
    user.userId,
    user.name,
    user.discordUserId,
    user.referralId,
    ...(user.storeIds || []),
    ...(user.homeDepotStoreIds || []),
    ...(user.targetStoreIds || []),
    ...(user.dollarGeneralStoreIds || []),
    ...(user.bestbuyStoreIds || []),
    ...(user.categories || []),
    ...(user.retailers || []),
  ].filter(Boolean).join(" ").toLowerCase(),
});

const hasActivePlan = (user: TableUser) => (user.retailers?.length || 0) > 0;

const toDate = (created: number | string) => {
  const value = typeof created === "number" ? (created > 10_000_000_000 ? created : created * 1000) : created;
  return new Date(value);
};

const relativeAge = (date: Date) => {
  const mins = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const planChipStyle = (retailer: string) => {
  if (retailer.startsWith("walmart")) return "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/25 dark:text-blue-300 dark:ring-blue-800";
  if (retailer.startsWith("home_depot")) return "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-900/25 dark:text-orange-300 dark:ring-orange-800";
  if (retailer.startsWith("target")) return "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/25 dark:text-red-300 dark:ring-red-800";
  if (retailer.startsWith("dollar_general")) return "bg-yellow-50 text-yellow-700 ring-yellow-200 dark:bg-yellow-900/25 dark:text-yellow-300 dark:ring-yellow-800";
  if (retailer.startsWith("bestbuy") || retailer.startsWith("best_buy")) return "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-900/25 dark:text-indigo-300 dark:ring-indigo-800";
  return "bg-gray-50 text-gray-600 ring-gray-200 dark:bg-dark-700 dark:text-dark-200 dark:ring-dark-600";
};

const formatPlan = (retailer: string) =>
  retailer.replace(/^home_depot/, "HD").replace(/^dollar_general/, "DG").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const DiscordIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

const TH = ({ children, className = "" }: { children?: React.ReactNode; className?: string }) => (
  <th className={`px-4 py-2 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400 ${className}`}>{children}</th>
);

type ActivityFilter = "all" | "active" | "inactive";

export default function UsersTable({ users, loading, onUserSelect, onCreateUser, planFilter, onPlanFilterChange }: UsersTableProps) {
  const [query, setQuery] = useState("");
  const [showNonFreeOnly, setShowNonFreeOnly] = useState(false);
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [selectedRetailer, setSelectedRetailer] = useState("all");
  const [referralFilter, setReferralFilter] = useState("all");
  const [referralPartners, setReferralPartners] = useState<AdminReferralPartner[]>([]);
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("created:desc");

  useEffect(() => { setCurrentPage(1); }, [planFilter, referralFilter]);

  useEffect(() => {
    if (!planFilter) return;
    setSelectedRetailer("all");
    setShowNonFreeOnly(false);
    setShowInactiveOnly(false);
  }, [planFilter]);

  useEffect(() => {
    getAdminReferralPartners().then(setReferralPartners).catch(() => setReferralPartners([]));
  }, []);

  const resetPage = () => setCurrentPage(1);
  const clearPlanFilter = () => onPlanFilterChange?.(null);

  const activityFilter: ActivityFilter = showNonFreeOnly ? "active" : showInactiveOnly ? "inactive" : "all";
  const setActivityFilter = (value: ActivityFilter) => {
    clearPlanFilter();
    setShowNonFreeOnly(value === "active");
    setShowInactiveOnly(value === "inactive");
    resetPage();
  };

  const partnerLabelByRef = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of referralPartners) {
      const label = p.name?.trim() || p.ref;
      map.set(p.ref.toLowerCase(), label === p.ref ? p.ref : `${label} (${p.ref})`);
    }
    return map;
  }, [referralPartners]);

  const allUsers = useMemo(() => users.map(mapToTableUser), [users]);

  const referralOptions = useMemo(() => {
    const counts = new Map<string, number>();
    let dealhawk = 0;
    for (const u of allUsers) {
      if (!u.referralId || isLegacyReferralId(u.referralId)) {
        dealhawk++;
        continue;
      }
      const key = u.referralId;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const refs = [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([ref, count]) => ({
        value: ref,
        label: partnerLabelByRef.get(ref.toLowerCase()) || ref,
        count,
      }));
    return { dealhawk, refs };
  }, [allUsers, partnerLabelByRef]);

  const matchingUsers = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const filtered = allUsers.filter((tableUser, i) => {
      if (planFilter && !userMatchesPlanFilter(users[i], planFilter)) return false;
      if (!planFilter) {
        if (showNonFreeOnly && !hasActivePlan(tableUser)) return false;
        if (showInactiveOnly && hasActivePlan(tableUser)) return false;
        if (selectedRetailer !== "all" && !tableUser.retailers.some((r) => r.startsWith(selectedRetailer))) return false;
      }
      if (referralFilter === DEALHAWK_REFERRAL_FILTER) {
        if (tableUser.referralId && !isLegacyReferralId(tableUser.referralId)) return false;
      } else if (referralFilter !== "all") {
        if (tableUser.referralId.toLowerCase() !== referralFilter.toLowerCase()) return false;
      }
      return terms.every((term) => tableUser.searchText.includes(term));
    });
    const direction = sortBy === "created:asc" ? 1 : -1;
    return filtered.sort((a, b) => direction * ((a.created ?? 0) - (b.created ?? 0)));
  }, [allUsers, users, query, planFilter, showNonFreeOnly, showInactiveOnly, selectedRetailer, referralFilter, sortBy]);

  const found = matchingUsers.length;
  const nbPages = Math.max(Math.ceil(found / pageSize), 1);
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= nbPages;
  const filteredUsers = useMemo(
    () => matchingUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [matchingUsers, currentPage, pageSize],
  );
  const rangeStart = found === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, found);

  const toggleSort = () => {
    setSortBy((s) => (s === "created:desc" ? "created:asc" : "created:desc"));
    resetPage();
  };

  const copyDiscordId = async (discordUserId: string) => {
    try { await navigator.clipboard.writeText(discordUserId); toast.success("Discord ID copied!"); }
    catch { const t = document.createElement("textarea"); t.value = discordUserId; document.body.appendChild(t); t.select(); document.execCommand("copy"); document.body.removeChild(t); toast.success("Discord ID copied!"); }
  };

  const hasAnyFilter = query.trim() || planFilter || activityFilter !== "all" || selectedRetailer !== "all" || referralFilter !== "all";

  if (loading) {
    return (
      <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 dark:border-red-400" />
        </div>
      </div>
    );
  }

  const selectClass = "appearance-none bg-transparent pl-3 pr-7 py-2 text-[13px] font-medium text-gray-700 dark:text-dark-100 rounded-lg ring-1 ring-gray-200 dark:ring-dark-600 hover:ring-gray-300 dark:hover:ring-dark-500 focus:outline-none focus:ring-2 focus:ring-red-500/60 transition-shadow cursor-pointer max-w-[220px] truncate";

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm overflow-hidden">
      {/* ── Toolbar ─────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100 dark:border-dark-700 space-y-3">
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-400" size={15} />
            <input
              type="text"
              placeholder="Search name, email, store ID, Discord…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); resetPage(); }}
              className="w-full pl-9 pr-8 py-2 text-[13px] rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-red-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none transition-all"
            />
            {query && (
              <button onClick={() => { setQuery(""); resetPage(); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-dark-200">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Activity segmented control */}
          <div className="flex items-center p-0.5 rounded-lg bg-gray-100 dark:bg-dark-700">
            {(["all", "active", "inactive"] as ActivityFilter[]).map((value) => (
              <button
                key={value}
                onClick={() => setActivityFilter(value)}
                className={`px-2.5 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${activityFilter === value
                  ? "bg-white dark:bg-dark-800 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-dark-300 hover:text-gray-700 dark:hover:text-dark-100"
                  }`}
              >
                {value}
              </button>
            ))}
          </div>

          <div className="relative">
            <select value={selectedRetailer} onChange={(e) => { clearPlanFilter(); setSelectedRetailer(e.target.value); resetPage(); }} className={selectClass}>
              <option value="all">All retailers</option>
              {AVAILABLE_RETAILERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-400 pointer-events-none" size={14} />
          </div>

          <div className="relative">
            <select value={referralFilter} onChange={(e) => { setReferralFilter(e.target.value); resetPage(); }} className={selectClass}>
              <option value="all">All referrals</option>
              <option value={DEALHAWK_REFERRAL_FILTER}>DealHawk ({referralOptions.dealhawk})</option>
              {referralOptions.refs.map((r) => (
                <option key={r.value} value={r.value}>{r.label} ({r.count})</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-400 pointer-events-none" size={14} />
          </div>

          <span className="ml-auto text-xs font-mono tabular-nums text-gray-400 dark:text-dark-400" title="matching / total">
            {found.toLocaleString()}<span className="text-gray-300 dark:text-dark-500">/{users.length.toLocaleString()}</span>
          </span>

          {onCreateUser && (
            <button onPointerDown={onCreateUser} className="px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors">
              Create user
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {(planFilter || referralFilter !== "all") && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {planFilter && (
              <button type="button" onClick={clearPlanFilter} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors">
                {USER_PLAN_FILTER_LABELS[planFilter]}
                <X size={12} className="opacity-70" />
              </button>
            )}
            {referralFilter !== "all" && (
              <button
                type="button"
                onClick={() => { setReferralFilter("all"); resetPage(); }}
                className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors"
              >
                {referralFilter === DEALHAWK_REFERRAL_FILTER
                  ? "DealHawk"
                  : (partnerLabelByRef.get(referralFilter.toLowerCase()) || referralFilter)}
                <X size={12} className="opacity-70" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Table ───────────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-100 dark:border-dark-700">
              <TH>User</TH>
              <TH>
                <button className="flex items-center gap-1 uppercase tracking-[0.14em] font-bold hover:text-gray-600 dark:hover:text-dark-200 transition-colors" onPointerDown={toggleSort}>
                  Created
                  {sortBy === "created:desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                </button>
              </TH>
              <TH>Status</TH>
              <TH>Plans</TH>
              <TH>Referral</TH>
              <TH className="text-right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-dark-700/60">
            {filteredUsers.map((u) => {
              const active = hasActivePlan(u);
              const date = u.created ? toDate(u.created) : null;
              return (
                <tr
                  key={u.userId}
                  onClick={() => onUserSelect(u.userId)}
                  className={`group cursor-pointer transition-colors ${u.banned ? "bg-red-50/40 dark:bg-red-900/10 hover:bg-red-50/70 dark:hover:bg-red-900/20" : "hover:bg-gray-50 dark:hover:bg-dark-700/40"}`}
                >
                  {/* User */}
                  <td className="px-4 py-2 max-w-[380px]">
                    <div className="flex items-center gap-2 min-w-0">
                      {u.name && (
                        <span className={`text-[13px] font-semibold shrink-0 ${u.banned ? "text-gray-400 dark:text-dark-400 line-through" : "text-gray-900 dark:text-white"}`}>{u.name}</span>
                      )}
                      <span className={`text-xs font-mono truncate ${u.name ? "text-gray-400 dark:text-dark-400" : u.banned ? "text-gray-400 dark:text-dark-400 line-through" : "text-gray-700 dark:text-dark-100 font-semibold"}`}>{u.userId}</span>
                      {u.banned && <Ban className="w-3.5 h-3.5 shrink-0 text-red-500" />}
                      {u.discordUserId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); copyDiscordId(u.discordUserId!); }}
                          className="shrink-0 text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 hover:scale-110 transition-all"
                          title="Copy Discord ID"
                        >
                          <DiscordIcon size={12} />
                        </button>
                      )}
                    </div>
                  </td>

                  {/* Created */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    {date ? (
                      <span className="text-xs" title={date.toLocaleString()}>
                        <span className="font-mono font-medium tabular-nums text-gray-700 dark:text-dark-100">{relativeAge(date)}</span>
                        <span className="text-gray-400 dark:text-dark-400"> · {date.toLocaleDateString("en-US", date.getFullYear() === new Date().getFullYear() ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" })}</span>
                      </span>
                    ) : <span className="text-xs text-gray-300 dark:text-dark-500">—</span>}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-dark-200">
                      <span className={`w-1.5 h-1.5 rounded-full ${u.banned ? "bg-red-500" : active ? "bg-emerald-500" : "bg-gray-300 dark:bg-dark-500"}`} />
                      {u.banned ? "Banned" : active ? "Active" : "Free"}
                    </span>
                  </td>

                  {/* Plans */}
                  <td className="px-4 py-2">
                    {u.banned || u.retailers.length === 0 ? (
                      <span className="text-xs text-gray-300 dark:text-dark-500">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 max-w-[260px]">
                        {u.retailers.map((retailer, idx) => (
                          <span key={`${retailer}-${idx}`} className={`inline-flex items-center px-1.5 py-px rounded text-[11px] font-semibold ring-1 ring-inset whitespace-nowrap ${planChipStyle(retailer)}`}>
                            {formatPlan(retailer)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Referral */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    {!u.referralId || isLegacyReferralId(u.referralId) ? (
                      <span className="text-xs text-gray-400 dark:text-dark-400">DealHawk</span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setReferralFilter(u.referralId); resetPage(); }}
                        className="block text-left max-w-[150px] text-xs font-semibold text-emerald-600 dark:text-emerald-400 truncate hover:underline"
                        title={`Filter by ${partnerLabelByRef.get(u.referralId.toLowerCase()) || u.referralId} (${u.referralId})`}
                      >
                        {(partnerLabelByRef.get(u.referralId.toLowerCase()) || u.referralId).split(" (")[0]}
                      </button>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onPointerDown={() => window.open(`https://us-east-2.console.aws.amazon.com/dynamodbv2/home?region=us-east-2#edit-item?itemMode=2&pk=${encodeURIComponent(u.userId)}&route=ROUTE_ITEM_EXPLORER&table=users`, "_blank")}
                        className="p-1 rounded-md text-gray-400 dark:text-dark-400 hover:text-gray-700 dark:hover:text-dark-100 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
                        title="Open in DynamoDB"
                      >
                        <Database size={14} />
                      </button>
                      <GodModeActivator targetUser={u} compact />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Empty state ─────────────────────────────────── */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-14">
          <SearchX className="w-8 h-8 mx-auto mb-3 text-gray-300 dark:text-dark-500" />
          <p className="text-sm font-medium text-gray-600 dark:text-dark-200">
            {hasAnyFilter ? `No users match${query.trim() ? ` "${query}"` : " the current filters"}` : "No users found"}
          </p>
          <p className="text-xs text-gray-400 dark:text-dark-400 mt-1">Try adjusting your filters or search terms</p>
        </div>
      )}

      {/* ── Pagination ──────────────────────────────────── */}
      {filteredUsers.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 dark:text-dark-400 font-mono tabular-nums">
              {rangeStart}–{rangeEnd} <span className="text-gray-300 dark:text-dark-500">of</span> {found.toLocaleString()}
            </span>
            <div className="relative">
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); resetPage(); }} className="appearance-none bg-transparent pl-2 pr-6 py-1 text-xs font-medium text-gray-500 dark:text-dark-300 rounded-md ring-1 ring-gray-200 dark:ring-dark-600 focus:outline-none focus:ring-2 focus:ring-red-500/60 cursor-pointer">
                {[10, 20, 30, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
              <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={12} />
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-mono tabular-nums text-gray-500 dark:text-dark-300 mr-1">{currentPage} / {nbPages}</span>
            <button
              className="p-1.5 rounded-lg ring-1 ring-gray-200 dark:ring-dark-600 text-gray-500 dark:text-dark-300 disabled:opacity-35 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={isFirstPage}
            >
              <ChevronLeft size={14} />
            </button>
            <button
              className="p-1.5 rounded-lg ring-1 ring-gray-200 dark:ring-dark-600 text-gray-500 dark:text-dark-300 disabled:opacity-35 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              onClick={() => setCurrentPage((p) => Math.min(nbPages, p + 1))}
              disabled={isLastPage}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
